import { apiClient } from "./client";
import { CLIENT_CONFIG } from "~/config/client";
import type { Addresses, User } from "~/types/user";

// ============================================================================
// API-specific Request/Response Types (matching backend structure)
// ============================================================================

export interface CreateAccountRequest {
  termsVersion: number;
}

export interface UpdateUserRequest {
  addresses?: Addresses;
  basicInfo?: object;
  defaultPaymentMethod?: string;
  havePaypalAuthorization?: boolean;
  fields?: object;
  allowPromoNotifications?: boolean;
  paymentToken?: object;
  notificationHistory?: any[];
  currency?: string;
  hasAcceptedAppRating?: boolean;
  platforms?: string[];
  settings?: object;
  refundType?: string;
  adultContentConsent?: boolean;
}

export interface UpdateUserResponse {
  user: User;
}

export interface GetDataResponse {
  user: User;
}

export interface CreateAccountResponse {
  user: User;
  token: string;
  accountDetails?: {
    email?: string;
    password?: string;
  };
}

export interface SignupRequest {
  email: string;
  termsVersion: number;
  invitationCode?: string;
}

export interface FinishDelayedSignupRequest {
  email: string;
  termsVersion: number;
  invitationCode?: string;
}

export interface ErrorResponse {
  status: number;
  type: "error";
  reason: string;
  code?: string;
}
export interface SuccessResponse {
  status: number;
  type: "success";
}

export interface SignupResponseForEmailSent extends SuccessResponse {
  message: string;
}
export interface SignupResponseForSuccess extends SuccessResponse {
  user: User;
  token: string;
}

export type SignupResponse = SignupResponseForEmailSent | SignupResponseForSuccess | ErrorResponse;

export interface EmailLoginRequest {
  email: string;
  token: string;
  termsVersion: number;
}

export interface EmailLoginResponseForSuccess extends SuccessResponse {
  user: User;
  token: string;
}

export type EmailLoginResponse = EmailLoginResponseForSuccess | ErrorResponse;

export interface FirebaseLoginRequest {
  idToken: string;
  email?: string;
  invitationCode?: string;
}

export interface FirebaseLoginResponse extends SuccessResponse {
  user: User;
  token: string;
}

export interface FacebookLoginRequest {
  profile: string | object;
  token: string;
  type?: string;
  firebaseAbTest?: object;
  termsVersion?: number;
  invitationCode?: string;
}

export interface FacebookLoginResponse extends SuccessResponse {
  user: User;
  token: string;
}

export interface MySearchKeywordsRequest {
  // Empty request body as per schema
}

export interface DtoHotKeywords {
  keywords: string[];
  timestamp: number;
}

export interface MySearchKeywordsResponse extends SuccessResponse {
  hotKeywords: DtoHotKeywords;
  recommendedKeywords: string[];
}

export interface CheckEmailExistsRequest {
  email: string;
}

export interface CheckEmailExistsResponse {
  exists: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Validates that a user API response has the expected structure
 */
function validateUserResponse(response: any): CreateAccountResponse {
  if (!response.user || !response.token) {
    console.error("Unexpected user API response structure:", response);
    throw new Error("Invalid response format: missing user or token");
  }

  return response;
}

/**
 * User API - Authentication endpoints
 */
export const userApi = {
  /**
   * Create a limited account for the user with minimal input
   */
  async createAccount(data: CreateAccountRequest): Promise<CreateAccountResponse> {
    const response = await apiClient.makeAuthRequest<any>("/user/create_account", data);
    return validateUserResponse(response);
  },

  async signup(data: SignupRequest): Promise<SignupResponse> {
    return await apiClient.makeAuthRequest<SignupResponse>("/user/signup", data);
  },

  async finishDelayedSignup(data: FinishDelayedSignupRequest): Promise<SignupResponse> {
    return await apiClient.makeAuthenticatedRequest<SignupResponse>("/user/finish_delayed_signup", { params: data });
  },

  async emailLogin(data: EmailLoginRequest): Promise<EmailLoginResponse> {
    return await apiClient.makeAuthRequest<EmailLoginResponse>("/user/email_login", data);
  },

  async firebaseLogin(data: FirebaseLoginRequest): Promise<FirebaseLoginResponse> {
    return await apiClient.makeAuthRequest<FirebaseLoginResponse>("/user/firebase_login", data);
  },

  async facebookLogin(data: FacebookLoginRequest): Promise<FacebookLoginResponse> {
    return await apiClient.makeAuthRequest<FacebookLoginResponse>("/user/facebook_login", data);
  },

  /**
   * Login using a stored token
   */
  async tokenLogin(token: string): Promise<CreateAccountResponse> {
    const response = await apiClient.makeAuthRequest<any>(
      "/user/token_login",
      { termsVersion: CLIENT_CONFIG.TERMS_VERSION },
      {
        Authorization: `Token ${token}`,
      }
    );
    return validateUserResponse(response);
  },

  async updateUser(data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.makeAuthenticatedRequest<UpdateUserResponse>("/user/update", { params: data });
    return response.user;
  },

  /**
   * Get fresh user data from the server
   */
  async getData(): Promise<User> {
    const response = await apiClient.makeAuthenticatedRequest<GetDataResponse>("/user/get_data");
    return response.user;
  },

  /**
   * Get user search keywords including history, hot keywords, and recommendations
   */
  async getMySearchKeywords(data: MySearchKeywordsRequest = {}): Promise<MySearchKeywordsResponse> {
    const response = await apiClient.makeAuthenticatedRequest<MySearchKeywordsResponse>("/user/my_search", {
      params: data,
    });
    return response;
  },

  /**
   * Check if an email exists in the system
   */
  async checkEmailExists(data: CheckEmailExistsRequest): Promise<CheckEmailExistsResponse> {
    const response = await apiClient.makeAuthenticatedRequest<CheckEmailExistsResponse>("/user/check_email_exists", {
      params: data,
    });
    return response;
  },
};
